import { useEffect, useRef } from "react";
import { ChevronRight } from "lucide-react";
import Avatar from "../Avatar";

export function remaining(item) {
  if (!item.runtime || typeof item.progress !== "number") return "RESUME";
  const secs = Math.max(60, Math.round(item.runtime * 60 * (1 - Math.min(99, item.progress) / 100)));
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${h ? `${h}:${pad(m)}` : m}:${pad(s)} REMAINING`;
}

function Thumb({ src, alt, big }) {
  return src
    ? <img src={src} alt={alt} loading="lazy" className={`${big ? "h-14 w-10" : "h-11 w-8"} flex-none rounded-sm object-cover`} />
    : <span className={`${big ? "h-14 w-10" : "h-11 w-8"} flex-none rounded-sm bg-[var(--bg-base)]`} />;
}

// A highlight-bar list. Rows are always directly tappable; `sel` follows the wheel.
export default function WheelList({ rows, sel, onHover, onPick, testid, menu = false, extra, onHold }) {
  const box = useRef(null);
  const held = useRef(false);
  const timer = useRef(null);
  // Long-press (500ms) a row -> onHold(i); the click that follows is swallowed.
  const pressHandlers = (i) => ({
    onPointerDown: () => { held.current = false; clearTimeout(timer.current); timer.current = setTimeout(() => { held.current = true; onHover(i); onHold(i); }, 500); },
    onPointerUp: () => clearTimeout(timer.current),
    onPointerLeave: () => clearTimeout(timer.current),
    onPointerCancel: () => clearTimeout(timer.current),
    onContextMenu: (e) => e.preventDefault(),
  });
  useEffect(() => {
    box.current?.querySelector(`[data-row="${sel}"]`)?.scrollIntoView({ block: "nearest" });
  }, [sel]);
  return (
    <ul ref={box} className="max-h-full overflow-y-auto" data-testid={testid}>
      {rows.map((r, i) => (
        <li key={r.key} className="relative">
          <button type="button" data-row={i} data-selected={i === sel ? "true" : "false"}
            onMouseMove={() => i !== sel && onHover(i)} onClick={() => { if (held.current) { held.current = false; return; } onPick(i); }}
            {...(onHold ? pressHandlers(i) : {})}
            className={`wheel-row flex w-full items-center gap-3 border-b border-[var(--border-subtle)] px-3 text-left ${menu ? "py-3.5" : "py-2"}`}
            data-testid={`${testid}-row-${r.key}`}>
            {!menu && <Thumb src={r.img} alt={r.label} />}
            <span className="min-w-0 flex-1">
              <span className={`block truncate font-semibold ${menu ? "text-base" : "text-sm"}`}>{r.label}</span>
              {r.sub && <span className="wheel-row-sub block truncate text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)]" data-testid={`${testid}-sub-${r.key}`}>{r.sub}</span>}
            </span>
            {menu && i === sel && r.img && <Thumb src={r.img} alt="" big />}
            {r.avatars?.length > 0 && (
              <span className={`${onHold ? "cursor-pointer" : "pointer-events-none"} flex flex-none items-center`} title={`Loved by ${r.avatars.map((p) => p.name).join(", ")}`} data-testid={`${testid}-avatars-${r.key}`}
                onClick={onHold ? (e) => { e.stopPropagation(); onHold(i); } : undefined}>
                {r.avatars.slice(0, 3).map((p, k) => <Avatar key={p.id} user={p} size={22} className={`border-2 border-[var(--bg-card)] ${k ? "-ml-2" : ""}`} />)}
              </span>
            )}
            {extra && i === sel && <span className="w-7 flex-none" />}
            <ChevronRight size={16} className="flex-none opacity-60" />
          </button>
          {extra && <span className="absolute right-9 top-1/2 z-10 -translate-y-1/2 text-[var(--wheel-bar-text)]">{extra(i)}</span>}
        </li>
      ))}
    </ul>
  );
}
